import React from 'react';
import { Loader } from 'src/components/core';
import styles from '../knowledge.module.scss';
import _ from 'lodash';
import { KnowledgeSection } from './knowledge-section';
import { ROUTES, CATEGORY_TYPES } from 'shared/constants';
import { KnowledgeCategoryEditorPopup } from '../knowledge-category/editor-popup/knowledge-category-editor-popup';
import update from 'immutability-helper';
import { useNavigate } from 'react-router-dom';
import { scrollTo } from 'src/utils';
import { ActionButton, NotFound } from 'src/components/common';
import { AddSectionButton } from 'src/components/common/add-section';
import classNames from 'classnames';
import { notification } from 'antd';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { hasExternalSyncDescendantById } from 'shared/store/selectors/knowledge.selector';
import { isPolicyTechSynced } from 'shared/utils/knowledge.utils';

const KnowledgeSectionListComponent = React.memo(function KnowledgeSectionListComponent({
  adminMode,
  list = null,
  isReady = true,
  fullPath = null,
  ownerIds = [],
  deleteCategory,
  detachMirroredCategory = () => {},
  updateSubcategoriesOrder = () => {},
  knowledgeSelectItem = () => {},
  id = null,
  tags,
  shared,
}) {
  const [categoryEntity, setCategory] = React.useState(null);
  const [sections, setSections] = React.useState(list);
  const mirrorLang = useSelector(getLang('KNOWLEDGE_CATEGORY'));
  const actionLang = useSelector(getLang('ACTION_BUTTON_TOOLTIPS'));

  const isCreateBlocked = useSelector(
    hasExternalSyncDescendantById(
      (sections ?? []).map(({ id }) => id),
      isPolicyTechSynced,
    ),
  );

  React.useEffect(() => {
    setSections(list);
  }, [list]);

  const navigate = useNavigate();

  const changeSectionOrder = (id, atIndex) => {
    const { section, index } = findSection(id);
    setSections(
      update(sections, {
        $splice: [
          [index, 1],
          [atIndex, 0, section],
        ],
      }),
    );
    // Update the server with the new index
    finishDragSection(id, atIndex);
  };

  const findSection = id => {
    const section = sections.find(i => i.id === id);
    return {
      section,
      index: sections.indexOf(section),
    };
  };

  const finishDragSection = async (categoryId, index) => {
    setTimeout(() => scrollTo(categoryId), 300);
    try {
      await updateSubcategoriesOrder({ id, categoryId, index });
    } catch (err) {
      notification.error({
        message:
          err?.code === 'CANNOT_REORDER_MIRROR_CHILDREN'
            ? mirrorLang.CANNOT_REORDER_MIRROR_CHILDREN
            : mirrorLang.REORDER_FAILED,
        placement: 'bottomRight',
      });
      // Rollback optimistic reorder
      setSections(list);
    }
  };

  const createCategoryAction = React.useCallback(
    ({ path, ownerIds, isSectionCreation = false, tags, labels, shared }) => {
      // TODO: add analytics

      setCategory({ path, ownerIds, isSectionCreation, tags, labels, shared });
    },
    [],
  );

  const editCategoryAction = React.useCallback(
    ({
      id,
      title,
      subtitle,
      ownerIds,
      path,
      isSectionCreation = false,
      tags,
      type,
      options,
      labels,
      shared,
    }) => {
      // TODO: add analytics

      setCategory({
        id,
        title,
        subtitle,
        ownerIds,
        path,
        isSectionCreation,
        tags,
        type,
        options,
        labels,
        shared,
      });
    },
    [],
  );

  const createSection = () => {
    // TODO: analytics
    createCategoryAction({
      path: fullPath,
      isSectionCreation: true,
      ownerIds,
      tags,
      shared,
    });
  };

  const editorFinish = React.useCallback(
    ({ id, type, isSectionCreation = false, isNew = false }) => {
      setCategory(null);
      if (id && !isSectionCreation) {
        if (type === CATEGORY_TYPES.KNOWLEDGE) {
          knowledgeSelectItem(id);
          return;
        }

        if (isNew) {
          navigate(ROUTES.CONTENT_ITEM.replace(':id', id));
        }
      }
    },
    [navigate, knowledgeSelectItem],
  );

  if (_.isNull(sections) || !isReady) {
    return (
      <div className={styles.loader}>
        <Loader />
      </div>
    );
  }

  if (sections.length === 0 && !adminMode) {
    return <NotFound />;
  }

  const callToAction = sections.length === 0 && adminMode && !categoryEntity;

  return (
    <div
      className={classNames(styles.root, { [styles.paddingBottom]: adminMode && !callToAction })}>
      {sections.map((item, index) => (
        <KnowledgeSection
          createCategory={createCategoryAction}
          deleteCategory={deleteCategory}
          detachMirroredCategory={detachMirroredCategory}
          editCategory={editCategoryAction}
          key={`content-item-${item.id}`}
          firstItem={index === 0}
          lastItem={index === sections.length - 1}
          index={index}
          changeSectionOrder={changeSectionOrder}
          sectionsLength={sections.length}
          {...item}
        />
      ))}

      {callToAction && <AddSectionButton onClick={createSection} />}
      {adminMode && !callToAction && (
        <ActionButton
          onClick={createSection}
          disabled={isCreateBlocked}
          labelText={
            isCreateBlocked ? `${actionLang.ADD_SECTION} — ${actionLang.READ_ONLY_SYNCED}` : null
          }
        />
      )}

      <KnowledgeCategoryEditorPopup
        open={!!categoryEntity}
        onFinish={editorFinish}
        entity={categoryEntity}
      />
    </div>
  );
});

export { KnowledgeSectionListComponent };
